import React, { useState, useEffect, useRef } from "react";
import { type UseFormRegisterReturn, Controller } from "react-hook-form";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { StyledInput } from "./styles";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type SelectOption = {
  label: string;
  value: string | number;
};

type InputProps = {
  id?: string;
  type: string;
  label: string | React.ReactNode;
  showLabel?: boolean;
  onChange?: () => void;
  error?: any;
  register: UseFormRegisterReturn;
  children?: any;
  disabled?: boolean;
  placeHolder?: string;
  helpText?: string;
  checked?: any;
  control?: any;
  value?: any;
  options?: SelectOption[];
  onOptionSelect?: () => void;
  dark?: boolean;
};

const Input = ({
  id,
  type,
  label,
  showLabel,
  onChange,
  register,
  error,
  disabled = false,
  placeHolder = null,
  helpText = null,
  control = null,
  checked = null,
  value = null,
  options = [],
  onOptionSelect,
  dark = false
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputFieldContainerClasses = `input-field ${type}-field ${dark ? "dark-mode" : ""}`;
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  // State for Custom Select
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  const displayHelpers = () => (
    <>
      {error && <span className="form-error">{error}</span>}
      {helpText && <div className="help-text">{helpText}</div>}
    </>
  );

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(event.target as Node)
      ) {
        setIsSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (type === "text" || type === "number" || type === "email") {
    return (
      <div className={inputFieldContainerClasses}>
        {showLabel && <label htmlFor={id}>{label}</label>}
        <StyledInput
          id={id}
          type={type}
          onChange={onChange}
          className={error ? "invalid-input" : ""}
          disabled={disabled}
          placeholder={placeHolder}
          defaultValue={value}
          {...register}
        />
        {displayHelpers()}
      </div>
    );
  } else if (type === "password") {
    return (
      <div className={inputFieldContainerClasses}>
        {showLabel && <label htmlFor={id}>{label}</label>}
        <div
          className="password-field-wrapper"
          style={{ position: "relative" }}
        >
          <StyledInput
            id={id}
            type={showPassword ? "text" : "password"}
            onChange={onChange}
            className={error ? "invalid-input" : ""}
            disabled={disabled}
            placeholder={placeHolder}
            {...register}
          />
          <button
            className="password-toggler"
            type="button"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <svg
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M22.6928 1.55018C22.3102 1.32626 21.8209 1.45915 21.6 1.84698L19.1533 6.14375C17.4864 5.36351 15.7609 4.96457 14.0142 4.96457C9.32104 4.96457 4.781 7.84644 1.11993 13.2641L1.10541 13.2854L1.09271 13.3038C0.970762 13.4784 0.967649 13.6837 1.0921 13.8563C3.79364 17.8691 6.97705 20.4972 10.3484 21.6018L8.39935 25.0222C8.1784 25.4101 8.30951 25.906 8.69214 26.1299L9.03857 26.3326C9.4212 26.5565 9.91046 26.4237 10.1314 26.0358L23.332 2.86058C23.553 2.47275 23.4219 1.97684 23.0392 1.75291L22.6928 1.55018ZM18.092 8.00705C16.7353 7.40974 15.3654 7.1186 14.0142 7.1186C10.6042 7.1186 7.07416 8.97311 3.93908 12.9239C3.63812 13.3032 3.63812 13.8561 3.93908 14.2354C6.28912 17.197 8.86102 18.9811 11.438 19.689L12.7855 17.3232C11.2462 16.8322 9.97333 15.4627 9.97333 13.5818C9.97333 11.2026 11.7969 9.27368 14.046 9.27368C15.0842 9.27368 16.0317 9.68468 16.7511 10.3612L18.092 8.00705ZM15.639 12.3137C15.2926 11.7767 14.7231 11.4277 14.046 11.4277C12.9205 11.4277 12 12.3906 12 13.5802C12 14.3664 12.8432 15.2851 13.9024 15.3624L15.639 12.3137Z"
                  fill="#cccccc"
                  fillRule="evenodd"
                />
                <path
                  d="M14.6873 22.1761C19.1311 21.9148 23.4056 19.0687 26.8864 13.931C26.9593 13.8234 27 13.7121 27 13.5797C27 13.4535 26.965 13.3481 26.8956 13.2455C25.5579 11.2677 24.1025 9.62885 22.5652 8.34557L21.506 10.2052C22.3887 10.9653 23.2531 11.87 24.0894 12.9239C24.3904 13.3032 24.3904 13.8561 24.0894 14.2354C21.5676 17.4135 18.7903 19.2357 16.0254 19.827L14.6873 22.1761Z"
                  fill="#cccccc"
                />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12"
                  stroke="#cccccc"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1 12C1 12 5 20 12 20C19 20 23 12 23 12"
                  stroke="#cccccc"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="#cccccc"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
        {displayHelpers()}
      </div>
    );
  } else if (type === "checkbox" || type === "radio") {
    return (
      <div className={inputFieldContainerClasses}>
        <label htmlFor={id}>
          <StyledInput
            className={error ? "invalid-input" : ""}
            type={type}
            id={id}
            disabled={disabled}
            value={value ?? ""}
            defaultChecked={checked}
            {...register}
          />
          {label}
        </label>
        {displayHelpers()}
      </div>
    );
  } else if (type === "mobile") {
    return (
      <Controller
        name={id}
        control={control}
        defaultValue={value}
        rules={{
          required: "Phone number is required",
          validate: (val) => {
            if (!val) return true; // Let 'required' handle empty values
            return isValidPhoneNumber(val) || "Invalid phone number";
          },
        }}
        render={({
          field: { onChange, value: rhfValue },
          fieldState,
          formState,
        }) => {
          // 1. Determine if we should SHOW the error
          // We only show it if there is an error AND (the user touched it OR submitted the form)
          const errorMsg = fieldState.error?.message;
          const hasError = !!errorMsg;
          const showError =
            hasError && (fieldState.isTouched || formState.isSubmitted);

          return (
            <div
              className={`${inputFieldContainerClasses} ${
                showError ? "invalid-phone-number" : ""
              }`}
            >
              {showLabel && <label htmlFor={id}>{label}</label>}

              <PhoneInput
                value={rhfValue || ""}
                onChange={onChange}
                defaultCountry="GB"
                id={id}
                placeholder={placeHolder || "Enter phone number"}
              />

              {/* 2. Display Error & Helpers directly here */}
              {displayHelpers()}
            </div>
          );
        }}
      />
    );
  } else if (type === "select" || type === "multiselect") {
    // === NEW SELECT LOGIC ===
    const isMulti = type === "multiselect";

    return (
      <Controller
        name={id}
        control={control}
        defaultValue={isMulti ? [] : ""}
        render={({ field: { onChange, value } }) => {
          // Helper to handle selection
          const handleSelectOption = (optionValue: string | number) => {
            if (isMulti) {
              const currentValues = Array.isArray(value) ? value : [];
              if (currentValues.includes(optionValue)) {
                // Remove if already selected
                onChange(currentValues.filter((v: any) => v !== optionValue));
                onOptionSelect(
                  currentValues.filter((v: any) => v !== optionValue),
                );
              } else {
                // Add if not selected
                onChange([...currentValues, optionValue]);
                onOptionSelect([...currentValues, optionValue]);
              }
            } else {
              // Single Select: Set value and close
              onChange(optionValue);
              setIsSelectOpen(false);
              onOptionSelect(optionValue);
            }
          };

          // Helper to get display label
          const getDisplayLabel = () => {
            if (isMulti) {
              if (!value || value.length === 0)
                return placeHolder || "Select options";
              return `${value.length} selected`; // Or map to labels if you prefer
            }
            // Single select
            const selectedOption = options.find((opt) => opt.value === value);
            return selectedOption
              ? selectedOption.label
              : placeHolder || "Select...";
          };

          return (
            <div
              className={inputFieldContainerClasses}
              ref={selectContainerRef}
            >
              {showLabel && <label className="input-label">{label}</label>}

              <div
                className="custom-select-wrapper"
                style={{ position: "relative", minWidth: "250px" }}
              >
                {/* The Trigger Box */}
                <div
                  className={`custom-select-trigger ${
                    error ? "invalid-input" : ""
                  } ${isSelectOpen ? "open" : ""}`}
                  onClick={() => !disabled && setIsSelectOpen(!isSelectOpen)}
                  // Add basic styling here or move to your StyledInput/CSS
                  style={{
                    border: `2px solid ${isSelectOpen ? "#01ECFF" : dark ? "rgba(254, 254, 254, 0.25)" : "#fff"}`,
                    padding: "10px",
                    cursor: disabled ? "not-allowed" : "pointer",
                    background: disabled ? "#f5f5f5" : dark ? "#211F45" : "#fff",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "#999",
                    fontSize: "14px",
                    transition: "all 0.2s ease"
                  }}
                >
                  <span>{getDisplayLabel()}</span>
                  <span className="arrow" style={{ height: "26px" }}>
                    {isSelectOpen ? (
                      <KeyboardArrowUpIcon style={{ color: "#999" }} />
                    ) : (
                      <KeyboardArrowDownIcon style={{ color: "#999" }} />
                    )}
                  </span>
                </div>

                {/* The Dropdown List */}
                {isSelectOpen && (
                  <div
                    className="custom-select-options"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 999,
                      background: "#fff",
                      border: "1px solid #ccc",
                      marginTop: "4px",
                      borderRadius: "8px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      fontSize: "14px",
                    }}
                  >
                    {options.map((opt) => {
                      const isSelected = isMulti
                        ? Array.isArray(value) && value.includes(opt.value)
                        : value === opt.value;

                      return (
                        <div
                          key={opt.value}
                          onClick={() => handleSelectOption(opt.value)}
                          className={`custom-option ${
                            isSelected ? "selected" : ""
                          }`}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            backgroundColor: isSelected ? "#f0f0f0" : "#fff",
                            borderBottom: "1px solid #eee",
                            color: isSelected ? "rgb(2, 0, 43)" : "#999",
                            fontWeight: isSelected ? "600" : "normal",
                          }}
                          // Add hover effect via CSS, inline hover is hard in React without state
                        >
                          {isMulti && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              style={{ marginRight: 8 }}
                            />
                          )}
                          {opt.label}
                        </div>
                      );
                    })}
                    {options.length === 0 && (
                      <div style={{ padding: "10px", color: "#999" }}>
                        No options
                      </div>
                    )}
                  </div>
                )}
              </div>
              {displayHelpers()}
            </div>
          );
        }}
      />
    );
  }
};

export default Input;
